import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { extractNestedAnswers } from "@/encounters/components/PostpartumEncounter/utils/postpartum";
import {
  PostpartumEncounterQuestionnaireURLs,
  PostpartumSexualActivityQuestionsDisplayNames,
  PostpartumSexualActivityQuestionsLinkIds,
} from "@/encounters/constants/postpartum-encounter";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";

const SexualActivityDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          PostpartumEncounterQuestionnaireURLs.SexualActivity,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  // If no sexual activity data has ever been recorded
  if (!currentVersion) {
    return (
      <Paper p="md" withBorder>
        <Stack mb="lg">
          <Title order={2}>Sexual Activity</Title>
        </Stack>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No sexual activity data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  // If sexual activity data exists but no previous version to compare against,
  // create an empty previous version to reuse the existing diff logic
  const effectivePreviousVersion =
    previousVersion || ({ item: [] } as unknown as QuestionnaireResponse);

  // Extract values for all sexual activity fields using extractNestedAnswers
  const previousAnswers = extractNestedAnswers(effectivePreviousVersion);
  const currentAnswers = extractNestedAnswers(currentVersion);

  const previousResumed =
    previousAnswers[PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityResumed]?.join(
      ", ",
    ) || "";
  const currentResumed =
    currentAnswers[PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityResumed]?.join(
      ", ",
    ) || "";

  const previousDiscomfort =
    previousAnswers[
      PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityDiscomfort
    ]?.join(", ") || "";
  const currentDiscomfort =
    currentAnswers[
      PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityDiscomfort
    ]?.join(", ") || "";

  const previousPain =
    previousAnswers[PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityPain]?.join(
      ", ",
    ) || "";
  const currentPain =
    currentAnswers[PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityPain]?.join(
      ", ",
    ) || "";

  const previousDryness =
    previousAnswers[PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityDryness]?.join(
      ", ",
    ) || "";
  const currentDryness =
    currentAnswers[PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityDryness]?.join(
      ", ",
    ) || "";

  const previousContraception =
    previousAnswers[
      PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityContraception
    ]?.join(", ") || "";
  const currentContraception =
    currentAnswers[
      PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityContraception
    ]?.join(", ") || "";

  const previousContraceptionMethod =
    previousAnswers[
      PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityContraceptionMethod
    ]?.join(", ") || "";
  const currentContraceptionMethod =
    currentAnswers[
      PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityContraceptionMethod
    ]?.join(", ") || "";

  const previousIUD =
    previousAnswers[PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityIUD]?.join(
      ", ",
    ) || "";
  const currentIUD =
    currentAnswers[PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityIUD]?.join(
      ", ",
    ) || "";

  const previousHormoneFree =
    previousAnswers[
      PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityHormoneFree
    ]?.join(", ") || "";
  const currentHormoneFree =
    currentAnswers[
      PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityHormoneFree
    ]?.join(", ") || "";

  const previousProgesteroneContaining =
    previousAnswers[
      PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityProgesteroneContaining
    ]?.join(", ") || "";
  const currentProgesteroneContaining =
    currentAnswers[
      PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityProgesteroneContaining
    ]?.join(", ") || "";

  const previousFuture =
    previousAnswers[PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityFuture]?.join(
      ", ",
    ) || "";
  const currentFuture =
    currentAnswers[PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityFuture]?.join(
      ", ",
    ) || "";

  const previousFutureWhen =
    previousAnswers[
      PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityFutureWhen
    ]?.join(", ") || "";
  const currentFutureWhen =
    currentAnswers[
      PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityFutureWhen
    ]?.join(", ") || "";

  const previousNotes =
    previousAnswers[PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityNotes]?.join(
      ", ",
    ) || "";
  const currentNotes =
    currentAnswers[PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityNotes]?.join(
      ", ",
    ) || "";

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Sexual Activity</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityResumed}
                </TextWrapper>
                <WordsDiff showRemoved current={currentResumed} previous={previousResumed} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityDiscomfort}
                </TextWrapper>
                <WordsDiff showRemoved current={currentDiscomfort} previous={previousDiscomfort} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityPain}
                </TextWrapper>
                <WordsDiff showRemoved current={currentPain} previous={previousPain} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityDryness}
                </TextWrapper>
                <WordsDiff showRemoved current={currentDryness} previous={previousDryness} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityContraception
                  }
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentContraception}
                  previous={previousContraception}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityContraceptionMethod
                  }
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentContraceptionMethod}
                  previous={previousContraceptionMethod}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityIUD}
                </TextWrapper>
                <WordsDiff showRemoved current={currentIUD} previous={previousIUD} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityHormoneFree
                  }
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentHormoneFree}
                  previous={previousHormoneFree}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityProgesteroneContaining
                  }
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentProgesteroneContaining}
                  previous={previousProgesteroneContaining}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityFuture}
                </TextWrapper>
                <WordsDiff showRemoved current={currentFuture} previous={previousFuture} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityFutureWhen}
                </TextWrapper>
                <WordsDiff showRemoved current={currentFutureWhen} previous={previousFutureWhen} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityNotes}
                </TextWrapper>
                <WordsDiff showRemoved current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityResumed}
                </TextWrapper>
                <WordsDiff current={currentResumed} previous={previousResumed} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityDiscomfort}
                </TextWrapper>
                <WordsDiff current={currentDiscomfort} previous={previousDiscomfort} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityPain}
                </TextWrapper>
                <WordsDiff current={currentPain} previous={previousPain} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityDryness}
                </TextWrapper>
                <WordsDiff current={currentDryness} previous={previousDryness} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityContraception
                  }
                </TextWrapper>
                <WordsDiff current={currentContraception} previous={previousContraception} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityContraceptionMethod
                  }
                </TextWrapper>
                <WordsDiff
                  current={currentContraceptionMethod}
                  previous={previousContraceptionMethod}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityIUD}
                </TextWrapper>
                <WordsDiff current={currentIUD} previous={previousIUD} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityHormoneFree
                  }
                </TextWrapper>
                <WordsDiff current={currentHormoneFree} previous={previousHormoneFree} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityProgesteroneContaining
                  }
                </TextWrapper>
                <WordsDiff
                  current={currentProgesteroneContaining}
                  previous={previousProgesteroneContaining}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityFuture}
                </TextWrapper>
                <WordsDiff current={currentFuture} previous={previousFuture} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityFutureWhen}
                </TextWrapper>
                <WordsDiff current={currentFutureWhen} previous={previousFutureWhen} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityNotes}
                </TextWrapper>
                <WordsDiff current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default SexualActivityDiff;
